import { createFileRoute, Link } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KPI } from '@/components/ui/kpi';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Receipt,
  Send,
  ArrowRight,
  Building2,
} from 'lucide-react';
import { fmtBRL } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { usePagamentosAggregate } from '@/lib/hooks/pagamentos';
import { useReceitasAggregate } from '@/lib/hooks/receitas';

export const Route = createFileRoute('/_app/')({
  component: DashboardHome,
});

const MES = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(new Date());

function currentCompetencia(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function DashboardHome() {
  const { user } = useAuth();
  const hasWorkspace = !!user?.workspaceName;
  const competencia = currentCompetencia();

  const pagAgg = usePagamentosAggregate(user?.effectiveWorkspaceId, competencia);
  const recAgg = useReceitasAggregate(user?.effectiveWorkspaceId, competencia);

  const receitas = Number(recAgg.data?.total ?? '0');
  const despesasConciliadas = Number(pagAgg.data?.conciliado.total ?? '0');
  const despesasPendentes = Number(pagAgg.data?.pendente.total ?? '0');
  const despesasTotais = despesasConciliadas + despesasPendentes;
  const saldo = receitas - despesasConciliadas;
  const pendentesCount = pagAgg.data?.pendente.count ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <Eyebrow>Bem-vindo de volta</Eyebrow>
        <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] leading-[1.0] text-fg mt-1">
          Olá, {user?.name?.split(' ')[0] ?? 'amigo'}.
        </h1>
      </header>

      {!hasWorkspace ? (
        <Card variant="clay" className="p-7">
          <Eyebrow className="opacity-70 !text-on-clay">Próximo passo</Eyebrow>
          <h2 className="font-display text-[30px] leading-[1.05] tracking-[-0.025em] font-bold mt-2">
            Selecione um workspace para começar
          </h2>
          <p className="text-sm opacity-80 mt-1.5 max-w-xl">
            Você está logado como administrador da plataforma. Para usar o app, entre num workspace
            específico ou crie um novo.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="ink" size="lg" asChild>
              <Link to="/master/workspaces">
                <Building2 className="h-4 w-4" strokeWidth={1.8} />
                Ir para Workspaces
                <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
              </Link>
            </Button>
          </div>
        </Card>
      ) : (
        <Card variant="mint" className="p-7">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Eyebrow className="opacity-70 !text-on-mint">Prestação · {MES}</Eyebrow>
              <h2 className="font-display text-[30px] leading-[1.05] tracking-[-0.025em] font-bold mt-2">
                {despesasTotais > 0 || receitas > 0
                  ? 'Sua próxima prestação está em construção'
                  : 'Comece cadastrando os lançamentos do mês'}
              </h2>
              <p className="text-sm opacity-80 mt-1.5 max-w-xl">
                {pendentesCount > 0
                  ? `${pendentesCount} pagamento${pendentesCount === 1 ? '' : 's'} pendente${pendentesCount === 1 ? '' : 's'} de conciliação. Finalize tudo antes de enviar.`
                  : 'Sua próxima prestação automatizada começa quando você tiver pagamentos, receitas e conciliação bancária prontos.'}
              </p>
            </div>
            <Pill tone="mint" dot className="hidden sm:inline-flex shrink-0">
              rascunho
            </Pill>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="primary" size="lg" asChild>
              <Link to="/lancamentos/pagamentos">
                <Receipt className="h-4 w-4" strokeWidth={1.8} />
                Cadastrar pagamentos
              </Link>
            </Button>
            <Button variant="ghost" size="lg" asChild>
              <Link to="/prestacoes">Ver histórico</Link>
            </Button>
          </div>
        </Card>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI
          label="Receitas no mês"
          value={fmtBRL(receitas)}
          delta={`${recAgg.data?.count ?? 0} ${(recAgg.data?.count ?? 0) === 1 ? 'lançamento' : 'lançamentos'}`}
          deltaDir={receitas > 0 ? 'up' : undefined}
          icon={TrendingUp}
        />
        <KPI
          label="Despesas no mês"
          value={fmtBRL(despesasTotais)}
          delta={pendentesCount > 0 ? `${pendentesCount} pendente${pendentesCount === 1 ? '' : 's'}` : '—'}
          deltaDir={despesasTotais > 0 ? 'down' : undefined}
          icon={TrendingDown}
        />
        <KPI
          tone="mint"
          label="Saldo no mês"
          value={fmtBRL(saldo)}
          delta={pendentesCount > 0 ? 'conciliação pendente' : 'tudo conciliado'}
          icon={Wallet}
        />
        <KPI tone="ink" label="Custo IA · mês" value={fmtBRL(0)} delta="0 chamadas" icon={Sparkles} />
      </section>

      <Card variant="default" className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Eyebrow>Atalhos</Eyebrow>
            <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
              O que você quer fazer agora?
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="default" asChild>
              <Link to="/lancamentos/pagamentos">
                <Receipt className="h-4 w-4" strokeWidth={1.8} />
                Novo pagamento
              </Link>
            </Button>
            <Button variant="default" asChild>
              <Link to="/conciliacao">Conciliar extrato</Link>
            </Button>
            <Button variant="default" asChild>
              <Link to="/prestacoes">
                <Send className="h-4 w-4" strokeWidth={1.8} />
                Iniciar prestação
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
